/**
 * Web GlobalStatus Provider
 *
 * This is a legacy component.
 * For referencing while developing new features, please use a Functional component.
 */

import {
  makeUniqueId,
  warn,
  convertJsxToString,
  slugify,
} from '../../shared/component-helper'

export class GlobalStatusProviderItem {
  constructor(id, props = null) {
    this.internalId = id
    if (props) {
      this.add(props)
    }
  }

  onUpdate(event) {
    // check for duplication first
    if (this._onUpdateEvents.filter((cb) => cb === event).length === 0) {
      this._onUpdateEvents.push(event)
    }
  }

  // force re-render of the given GlobalStatus component
  forceRerender(
    globalStatus,
    props,
    { bufferDelay = 0, isEmpty = false } = {}
  ) {
    const run = () => {
      this._onUpdateEvents.forEach((event) => {
        if (typeof event === 'function') {
          event(globalStatus, props, { isEmpty })
        }
      })
    }

    if (bufferDelay > 0) {
      clearTimeout(this._bufferDelayId)
      this._bufferDelayId = setTimeout(run, bufferDelay) // delay the sum & rerender, in case we change the state in the same frame
    } else {
      run()
    }
  }

  init(props) {
    return this.add(props, { preventRerender: true })
  }

  add(props, opts = {}) {
    this.remove('internal-close', { preventRerender: true })

    // make copy
    const newProps = { ...props }

    // make sure we have a status id
    if (!newProps.statusId) {
      newProps.statusId = makeUniqueId()
    }

    // also, set show to true
    if (typeof newProps.show === 'undefined') {
      newProps.show = true
    }

    // make it possible to send in children as the text
    if (newProps.children) {
      newProps.text = newProps.children
      delete newProps.children
    }

    // replace the props if exists
    const stackIndex = this.stack.findIndex(
      (cur) => cur.statusId === newProps.statusId
    )
    if (stackIndex > -1) {
      this.stack[stackIndex] = newProps
    } else {
      // add the new props
      this.stack.push(newProps)
    }

    const globalStatus = GlobalStatusProvider.combineMessages(this.stack)

    if (!opts?.preventRerender) {
      this.forceRerender(globalStatus, props, {
        bufferDelay: props?.bufferDelay > -1 ? props.bufferDelay : 0,
      })
    }

    return globalStatus
  }

  get(statusId) {
    return this.stack.find((cur) => cur.statusId === statusId)
  }

  update(statusId, newProps, opts = {}) {
    const item = this.get(statusId)
    if (!item) {
      this.add(newProps, { preventRerender: true })
    } else {
      this.stack = this.stack.map((cur, i, arr) => {
        if (statusId ? cur.statusId === statusId : i === arr.length - 1) {
          // if (!statusId) {
          //   // newProps = { ...newProps }
          //   delete newProps.statusId
          // }
          return { ...cur, ...newProps }
        }

        return cur
      })
    }

    this.restack(statusId)

    const globalStatus = GlobalStatusProvider.combineMessages(this.stack)

    if (!opts?.preventRerender) {
      this.forceRerender(globalStatus, null, {
        bufferDelay: newProps?.bufferDelay > -1 ? newProps.bufferDelay : 1,
      })
    }
  }

  restack(statusId) {
    const item = this.get(statusId)

    // re-stack,so the new one is the latest
    if (item) {
      this.stack = this.stack.filter((cur) => {
        return cur.statusId !== statusId
      })
      this.stack.push(item)
    }
  }

  remove(statusId, opts = {}) {
    if (statusId) {
      this.stack = this.stack.filter((cur) => {
        return cur.statusId !== statusId
      })
      const globalStatus = GlobalStatusProvider.combineMessages(this.stack)

      if (!opts?.preventRerender) {
        this.forceRerender(globalStatus, null, {
          bufferDelay: opts?.bufferDelay > -1 ? opts.bufferDelay : 1,
        })
      }
    }
  }

  empty() {
    this._onUpdateEvents.forEach((cb, i) => {
      this._onUpdateEvents[i] = null
    })
    this._onUpdateEvents = []
    this._onReadyEvents.forEach((cb, i) => {
      this._onReadyEvents[i] = null
    })
    this._onReadyEvents = []
  }

  // to remove this provider item
  unbind() {
    this.empty()
    GlobalStatusProvider.remove(this.internalId)
  }

  isReady() {
    this._onReadyEvents = this._onReadyEvents.filter(
      ({ status, cb }, i) => {
        if (typeof cb === 'function') {
          cb(status)
        }
        this._onReadyEvents[i] = null
        return false
      }
    )

    return true
  }

  addOnReady(status, cb) {
    this._onReadyEvents.push({ status, cb })
  }

  stack = [] // the "layers" with
  globalStatus = {} // summary of all stacks
  _onUpdateEvents = [] // for the "onUpdate" events
  _onReadyEvents = [] // for startup events
}

// The meaning with this is that we can force a rerender without sharing the same context
class GlobalStatusProvider {
  static providers = {}

  static create = (id = 'main', props = null) => {
    return (GlobalStatusProvider.providers[id] =
      new GlobalStatusProviderItem(id, props))
  }

  static init(id = 'main', onReady = null, props = null) {
    const existingStatus = GlobalStatusProvider.get(id)

    if (existingStatus) {
      if (props) {
        existingStatus.add(props)
      }
      if (typeof onReady === 'function') {
        onReady(existingStatus)
      }
      return existingStatus
    }

    const newStatus = GlobalStatusProvider.create(id, props)

    if (onReady) {
      // send along the new status
      newStatus.addOnReady(newStatus, onReady)
    }

    if (id !== 'main') {
      warn(`No <GlobalStatus ${`id="${id}"`} /> found.`)
    }

    return newStatus
  }

  static get(id = 'main') {
    return GlobalStatusProvider.providers[id] || null
  }

  static remove(id = 'main') {
    if (GlobalStatusProvider.providers[id]) {
      delete GlobalStatusProvider.providers[id]
    }
  }

  static prepareItemWithStatusId(item, statusId = null) {
    if (typeof item === 'string') {
      item = { text: item }
    }

    if (!item.itemId) {
      if (statusId && statusId !== 'status-main') {
        item.itemId = statusId
      } else {
        if (item?.text) {
          item.itemId = slugify(convertJsxToString(item.text))
        } else {
          item.itemId = slugify(item)
        }
      }
    }

    return item
  }

  static combineMessages(stack) {
    const globalStatus = stack.reduce((acc, cur) => {
      // make a copy, because items are read-only
      cur = { ...cur }

      if (typeof cur.items === 'string' && cur.items[0] === '[') {
        cur.items = JSON.parse(cur.items)
      }

      // if there is only one item, put it into the array
      if (cur.item) {
        if (typeof cur.item === 'string' && cur.item[0] === '{') {
          cur.item = JSON.parse(cur.item)
        }
        // make sure we have an array of items
        cur.items = cur.items || []
        cur.items.push(cur.item)
      }

      // merge items from prev stack into the current
      if (cur.items) {
        cur.items = cur.items.reduce((_acc, item) => {
          // only a fallback and to make sure we have
          item = GlobalStatusProvider.prepareItemWithStatusId(item)

          const foundAtIndex = _acc.findIndex(
            ({ itemId }) => itemId === item.itemId
          )
          if (foundAtIndex > -1) {
            _acc[foundAtIndex] = item
          } else {
            _acc.push(item)
          }

          return _acc
        }, acc.items || []) // here we use the items from the prev stack
      }

      // merge the prev stack with the current
      Object.assign(acc, cur)

      return acc
    }, {})

    return globalStatus
  }
}

// add a fallback, in case we don't run this inside the same React instance
if (typeof window !== 'undefined') {
  window.GlobalStatusProvider = GlobalStatusProvider
}

GlobalStatusProvider._supportsSpacingProps = true

export default GlobalStatusProvider
